import React, { useEffect, useState } from 'react';
import { Alert, Pagination, Spin } from 'antd';
import { useGetDicomsQuery } from 'services/exploreService';
import { useUrlFilters } from 'hooks/useUrlFilters';
import FilterBar from 'components/explore/FilterBar';
import { DicomCard, Empty, PageHead, Skeleton, fmt } from 'components/explore';
import DicomInspector from 'components/explore/DicomInspector';

const PAGE_SIZE = 48;

const Dicoms: React.FC = () => {
  const { filters, page, update, clear, activeCount } = useUrlFilters(PAGE_SIZE);
  const { data, isLoading, isFetching, error } = useGetDicomsQuery(filters);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => setOpen(null), [data]);

  return (
    <>
      <FilterBar
        filters={filters}
        update={update}
        clear={clear}
        activeCount={activeCount}
        total={data?.total}
        unit="DICOMs"
        level="dicom"
        placeholder="DICOM ICID, study ICID or foreign id"
      />
      <div className="ax-page">
        <PageHead
          title="DICOMs"
          lede="First frame of every clip. Hover to play the loop, click to inspect (← → to step). Badges: AI view (blue), tech view (orange), SEG / KP / Q annotations."
        />
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Could not load DICOMs"
            description="The Explorer API returned an error."
          />
        ) : isLoading ? (
          <div className="ax-gallery">
            {Array.from({ length: 12 }).map((_, i) => (
              <div className="ax-card" key={i}>
                <Skeleton h={160} />
                <div className="meta">
                  <Skeleton h={12} w="70%" />
                </div>
              </div>
            ))}
          </div>
        ) : !data?.items.length ? (
          <div className="ax-panel">
            <Empty title="Nothing matches these filters">
              Try removing a filter, or pick a view on the left.
            </Empty>
          </div>
        ) : (
          <Spin spinning={isFetching} delay={150}>
            <div className="ax-gallery">
              {data.items.map((d, i) => (
                <DicomCard key={d.uuid} d={d} showStudy onOpen={() => setOpen(i)} />
              ))}
            </div>
          </Spin>
        )}
      </div>
      {data && data.total > PAGE_SIZE && (
        <div className="ax-pager">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            showSizeChanger={false}
            showTotal={(t, r) => `${fmt(r[0])}–${fmt(r[1])} of ${fmt(t)}`}
            onChange={p => {
              update({ page: p });
              document.querySelector('.ax-main')?.scrollTo({ top: 0 });
            }}
          />
        </div>
      )}
      {data && (
        <DicomInspector
          items={data.items}
          index={open}
          onIndex={setOpen}
          offset={data.offset}
          total={data.total}
        />
      )}
    </>
  );
};

export default Dicoms;
